import React, { memo, useEffect, useState } from "react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { Percentages } from "../../models/result";
import { getFormPercentages, getSectionPercentages } from "../../services/resultsService";
import geoJson from "./geoJson.json";

// @ts-ignore
import Gradient from "javascript-color-gradient";
import ReactTooltip from "react-tooltip";
import { ICountryResults, IFormResultDataDelivery } from "../../models/formResultDataDelivery";
import { IParameterAnswer } from "../../models/parameterAnswer";
import { initParameter, IParameter, NetworkCode } from "../../models/parameter";
import { ForceModal } from "../ImplementationMaps/ForceModal";
import "./MapChart.css";
import { useSelector } from "react-redux";
import { AppState } from "../../store/configureStore";
import { SummaryModal } from "../ImplementationMaps/SummaryModal";

const getPercentageData = async (
    categoryData: { name: string; value: string }[],
    formId: string,
    activeSection?: string
) => {
    let percentages: Percentages;
    let newData = [...categoryData];

    if (activeSection) {
        percentages = await getSectionPercentages(activeSection);
    } else {
        percentages = await getFormPercentages(formId);
    }

    newData.forEach(cd => {
        if (cd.value !== "Submitted" && percentages[cd.name]) {
            cd.value = Math.round(percentages[cd.name]).toString();
        }
    });

    return newData;
};

const getCategoryData = (data: IFormResultDataDelivery, activeSection?: string) => {
    if (data.sectionResults.length === 0) {
        return [];
    }

    let targetData: ICountryResults[];

    if (activeSection) {
        let activeSectionResults = data.sectionResults.find(sr => sr.sectionId === activeSection);
        targetData = activeSectionResults?.countryResults || [];
    } else {
        targetData = data.formResults;
    }

    return targetData.map(cr => {
        return {
            name: cr.countryName,
            value: cr.resultStatus,
        };
    });
};

interface IMapProps {
    dataDeliveryResults?: IFormResultDataDelivery;
    activeSection?: string;
    isPercentage: boolean;
    parameterAnswers?: IParameterAnswer[];
    parameters?: IParameter[];
    parameterGuid?: string;
    nonInvolvedCountries?: string[];
    networkCode?: NetworkCode;
    refreshTrigger?: () => void;
}

const MapChart = ({
    dataDeliveryResults,
    activeSection,
    isPercentage,
    parameterAnswers,
    parameters,
    parameterGuid,
    nonInvolvedCountries,
    networkCode,
    refreshTrigger,
}: IMapProps) => {
    const user = useSelector((state: AppState) => state.user);
    const [data, setData] = useState<{ name: string; value: string }[]>([]);
    const [colors, setColors] = useState<{ [id: string]: string }>({});
    const [tooltipCountry, setTooltipCountry] = useState("");
    const [tooltipInfo, setTooltipInfo] = useState("");
    const [tooltipRemark, setTooltipRemark] = useState("");
    const [forceModalOpen, setForceModalOpen] = useState<boolean>(false);
    const [summaryModalOpen, setSummaryModalOpen] = useState<boolean>(false);
    const [selectedAnswer, setSelectedAnswer] = useState<IParameterAnswer | undefined>(undefined);

    useEffect(() => {
        async function getNewData() {
            if (!dataDeliveryResults) {
                return;
            }
            let newData = getCategoryData(dataDeliveryResults, activeSection);
            if (isPercentage && dataDeliveryResults) {
                newData = await getPercentageData(
                    newData,
                    dataDeliveryResults.formId,
                    activeSection
                );
            }
            setData(newData);
        }
        getNewData();
    }, [activeSection, isPercentage, dataDeliveryResults]);

    useEffect(() => {
        async function getNewData() {
            if (
                (!!parameters && parameters.length === 0) ||
                (!!parameterAnswers && parameterAnswers.length === 0)
            ) {
                setData([]);
                return;
            }

            let notInvolvedData: {
                name: string;
                value: string;
            }[] = [];
            // color not-involved countries
            if (nonInvolvedCountries) {
                notInvolvedData = nonInvolvedCountries?.map(country => {
                    return {
                        name: country,
                        value: "non-eu",
                    };
                });
            }
            // parameter answers
            if (parameterAnswers && parameters) {
                const newData = parameterAnswers?.map(pa => {
                    return {
                        name: pa.countryName,
                        value: getParameterAnswer(pa, parameters),
                    };
                });
                setData(newData?.concat(notInvolvedData) ?? []);
            }
        }
        getNewData();
    }, [parameters, parameterAnswers]);

    const getParameterAnswer = (parameterAnswer: IParameterAnswer, parameters?: IParameter[]) => {
        if (!parameters) {
            return "";
        }
        const parameter = parameters?.find(p => p.guid === parameterAnswer.parameterGuid);
        if (!parameter) {
            return "";
        }

        let remark = parameterAnswers?.find(
            p => p.countryName === parameterAnswer.countryName
        )?.remark;
        // remark only for selected parameter
        if (remark && parameterGuid) {
            remark = `\nRemark: ${remark}\n`;
        } else {
            remark = "";
        }

        let answerText = "";
        switch (parameterAnswer.forcedAnswerValue ?? parameterAnswer.answerValue) {
            case 1:
                answerText = parameter?.answer1text;
                break;
            case 2:
                answerText = parameter?.answer2text;
                break;
            case 3:
                answerText = parameter?.answer3text;
                break;
            case 4:
                answerText = parameter?.answer4text;
                break;
            case 5:
                answerText = parameter?.answer5text;
                break;
            case 6:
                answerText = parameter?.answer6text;
                break;
            case 7:
                answerText = parameter?.answer7text;
                break;
            default:
                answerText = "";
        }

        return `${
            parameterAnswer.forcedAnswerValue ?? parameterAnswer.answerValue
        }${answerText} ${remark}`;
    };

    useEffect(() => {
        const colorGradient = new Gradient();
        colorGradient.setGradient("#0d4da1", "#ffd500");
        colorGradient.setMidpoint(101);
        let colorArray = colorGradient.getArray();
        let colorsMap: { [id: string]: string } = {};
        colorsMap["New"] = "#0D4DA1";
        colorsMap["Partial"] = "#FFD500";
        colorsMap["Submitted"] = "#00A990";
        colorsMap["default"] = "#e9e9ea";

        // parameter colors
        colorsMap["a1"] = "#a6a6a6";
        colorsMap["a2"] = "#ffffff";
        colorsMap["a3"] = "#fd0017";
        colorsMap["a4"] = "#fd8d29";
        colorsMap["a5"] = "#febf27";
        colorsMap["a6"] = "#93d057";
        colorsMap["a7"] = "#15b053";
        colorsMap["non-eu"] = "#5196d7";

        colorArray.forEach((color: string, i: number) => {
            colorsMap[i.toString()] = color;
        });
        setColors(colorsMap);
    }, []);

    const setTooltipContent = (d?: { name: string; value: string }) => {
        if (!d) {
            return;
        }
        setTooltipCountry(d.name);
        setTooltipInfo(d.value !== "non-eu" ? d.value.substring(1).split("\n")[0] : "");
        if (isPercentage) {
            switch (d.value.toLowerCase()) {
                case "new":
                    setTooltipInfo("0%");
                    break;
                case "submitted":
                    setTooltipInfo("Submitted");
                    break;
                default:
                    setTooltipInfo(`${d.value}%`);
            }
        } else {
            switch (d.value.toLowerCase()) {
                case "submitted":
                    setTooltipInfo("Submitted");
                    break;
                case "partial":
                    setTooltipInfo("Partially filled");
                    break;
                case "new":
                    setTooltipInfo("No data delivered");
            }
        }
        setTooltipRemark(d.value.substring(1).includes("Remark") ? d.value.split("\n")[1] : "");
    };

    const clearTooltipContent = () => {
        setTooltipCountry("");
        setTooltipInfo("");
    };

    return (
        <>
            <ComposableMap
                data-tip=""
                projection="geoMercator"
                projectionConfig={{
                    rotate: [-20, -53.0, 5],
                    scale: 900,
                }}
                zoomAndPan=""
                className="map-chart"
            >
                <Geographies geography={geoJson}>
                    {({ geographies }) => {
                        const map = geographies.map((geo, i) => {
                            const d = data.find(x => x.name === geo.properties.NAME);

                            if (geo.properties.ALLOW_SKIP && !d && dataDeliveryResults) {
                                return <React.Fragment key={i}></React.Fragment>;
                            }

                            let colorFill = "";
                            if (d?.value) {
                                if (d.value === "non-eu") {
                                    colorFill = colors["non-eu"];
                                } else if (parameterAnswers) {
                                    colorFill = colors[`a${d.value[0]}`];
                                } else {
                                    colorFill = colors[d.value];
                                }
                            } else {
                                colorFill = colors["default"];
                            }

                            return (
                                <Geography
                                    key={geo.rsmKey}
                                    geography={geo}
                                    fill={colorFill}
                                    stroke="#a4a9b2"
                                    onMouseEnter={() => {
                                        setTooltipContent(d);
                                    }}
                                    onMouseLeave={() => {
                                        clearTooltipContent();
                                    }}
                                    style={{
                                        default: { outline: "none" },
                                        // set pointer if value exists and is not non-eu
                                        hover: {
                                            outline: "none",
                                            cursor:
                                                d?.value && d.value !== "non-eu"
                                                    ? "pointer"
                                                    : "default",
                                        },
                                        pressed: { outline: "none" },
                                    }}
                                    onClick={() => {
                                        setSelectedAnswer(
                                            parameterAnswers?.find(
                                                pa => pa.countryName === geo.properties.NAME
                                            )
                                        );
                                        !parameters?.find(p => p.guid === parameterGuid)?.summary &&
                                            parameterGuid &&
                                            user.role &&
                                            setForceModalOpen(true);
                                        parameters?.find(p => p.guid === parameterGuid)?.summary &&
                                            parameterGuid &&
                                            user.role &&
                                            setSummaryModalOpen(true);
                                    }}
                                />
                            );
                        });
                        return map;
                    }}
                </Geographies>
            </ComposableMap>
            <ReactTooltip type={"light"} className="map-tooltip">
                {tooltipCountry && (
                    <>
                        <b>{tooltipCountry}</b>
                        <div className="info">{tooltipInfo !== "" && <>{tooltipInfo}</>}</div>
                        <div className="remark">{tooltipRemark !== "" && <>{tooltipRemark}</>}</div>
                    </>
                )}
            </ReactTooltip>
            {selectedAnswer && forceModalOpen && (
                <ForceModal
                    modalOpen={forceModalOpen}
                    closeModal={() => setForceModalOpen(false)}
                    parameterAnswer={selectedAnswer}
                    parameter={
                        parameters?.find(p => p.guid === selectedAnswer.parameterGuid) ??
                        initParameter
                    }
                    networkCode={networkCode ?? NetworkCode.Unknown}
                    refreshTrigger={refreshTrigger}
                ></ForceModal>
            )}
            {selectedAnswer && summaryModalOpen && (
                <SummaryModal
                    modalOpen={summaryModalOpen}
                    closeModal={() => setSummaryModalOpen(false)}
                    parameter={
                        parameters?.find(p => p.guid === selectedAnswer.parameterGuid) ??
                        initParameter
                    }
                    selectedCountryName={selectedAnswer.countryName}
                    parameterAnswer={
                        data
                            .find(pa => pa.name === selectedAnswer.countryName)
                            ?.value.substring(1)
                            .split("\n")[0] ?? ""
                    }
                ></SummaryModal>
            )}
        </>
    );
};

export default memo(MapChart);
